import moment from 'moment'

import {
  createInitialInstallationCustomerDuty,
  createInitialInstallationLineItems,
  createInitialLineItems,
  INITIAL_INSTALLATION_CONTRACT,
  INITIAL_INSTALLATION_CRANE_SCHEDULE,
  INITIAL_INSTALLATION_FINANCE,
  INITIAL_INSTALLATION_PILING_SITE,
  INITIAL_INSTALLATION_RIG_FOOTER,
  INITIAL_INSTALLATION_SIGNATURES,
  type DeliverySiteState,
  type HeaderDocumentState,
  type InstallationLineItem,
  type LineItem,
} from './types'

const parseDate = (value: unknown): Date | null => {
  if (!value) return null
  const m = moment(value as moment.MomentInput)
  return m.isValid() ? m.toDate() : null
}

const toIsoDate = (value: Date | null): string | null => {
  if (!value || !moment(value).isValid()) return null
  return moment(value).toISOString()
}

const mapHeaderFromFlatApi = (data: any): HeaderDocumentState => ({
  documentNo: data?.documentNo ?? '',
  mapNo: data?.mapNo ?? '',
  department: data?.department ?? '',
  documentDate: parseDate(data?.documentDate),
  customerTitle: data?.customerTitle ?? '',
  division: data?.division ?? '',
  productionOrderNo: data?.productionOrderNo ?? '',
})

const mapHeaderToFlatApi = (doc: HeaderDocumentState) => ({
  documentNo: doc.documentNo || null,
  mapNo: doc.mapNo || null,
  department: doc.department || null,
  documentDate: toIsoDate(doc.documentDate),
  customerTitle: doc.customerTitle || null,
  division: doc.division || null,
  productionOrderNo: doc.productionOrderNo || null,
})

const mergeHeaderPrefill = (
  header: HeaderDocumentState,
  prefill?: SurveyPrefill
): HeaderDocumentState => ({
  ...header,
  customerTitle: header.customerTitle || prefill?.customerTitle || '',
  division: header.division || prefill?.division || '',
  productionOrderNo: header.productionOrderNo || prefill?.productionOrderNo || '',
  documentDate: header.documentDate ?? prefill?.documentDate ?? null,
})

const mapLineItemsFromApi = (items: any[] | undefined): LineItem[] => {
  if (!items?.length) return createInitialLineItems()
  return items.map((row, index) => ({
    id: row?.id ?? `${Date.now()}-${index}`,
    no: row?.no ?? '',
    description: row?.description ?? '',
    typeModel: row?.typeModel ?? '',
    quantity: row?.quantity != null ? String(row.quantity) : '',
  }))
}

const mapLineItemsToApi = (items: LineItem[]) =>
  items.map((row, index) => ({
    no: row.no || String(index + 1),
    description: row.description || null,
    typeModel: row.typeModel || null,
    quantity: row.quantity || null,
  }))

const mapInstallationLineItemsFromApi = (items: any[] | undefined): InstallationLineItem[] => {
  if (!items?.length) return createInitialInstallationLineItems()
  return items.map((row, index) => ({
    id: row?.id ?? `${Date.now()}-${index}`,
    no: row?.no ?? '',
    description: row?.description ?? '',
    typeModel: row?.typeModel ?? '',
    quantity: row?.quantity != null ? String(row.quantity) : '',
    receive: Boolean(row?.receive),
    hire: Boolean(row?.hire),
  }))
}

const mapInstallationLineItemsToApi = (items: InstallationLineItem[]) =>
  items.map((row, index) => ({
    no: row.no || String(index + 1),
    description: row.description || null,
    typeModel: row.typeModel || null,
    quantity: row.quantity || null,
    receive: row.receive,
    hire: row.hire,
  }))

export interface WorkOpeningNoticeShippingForm {
  headerDoc: HeaderDocumentState
  sendTo: string
  items: LineItem[]
  selectedPileSize: string
  deliverySite: DeliverySiteState
  payment: {
    status: string
    depositPercent: string
    promissoryDays: string
    notCompletedReason: string
  }
  deliveryNotes: string
}

export interface WorkOpeningNoticeInstallationForm {
  headerDoc: HeaderDocumentState
  sendTo: string
  items: InstallationLineItem[]
  selectedPileSize: string
  pilingSite: typeof INITIAL_INSTALLATION_PILING_SITE
  craneSchedule: typeof INITIAL_INSTALLATION_CRANE_SCHEDULE
  customerDuty: ReturnType<typeof createInitialInstallationCustomerDuty>
  contract: typeof INITIAL_INSTALLATION_CONTRACT
  payment: typeof INITIAL_INSTALLATION_FINANCE
  signatures: typeof INITIAL_INSTALLATION_SIGNATURES
  rigFooter: typeof INITIAL_INSTALLATION_RIG_FOOTER
}

export type SurveyPrefill = {
  customerTitle?: string
  division?: string
  productionOrderNo?: string
  documentDate?: Date | null
}

const buildPrefilledHeader = (prefill?: SurveyPrefill): HeaderDocumentState => ({
  documentNo: '',
  mapNo: '',
  department: '',
  documentDate: prefill?.documentDate ?? null,
  customerTitle: prefill?.customerTitle ?? '',
  division: prefill?.division ?? '',
  productionOrderNo: prefill?.productionOrderNo ?? '',
})

export const createInitialShipping = (prefill?: SurveyPrefill): WorkOpeningNoticeShippingForm => ({
  headerDoc: buildPrefilledHeader(prefill),
  sendTo: '',
  items: createInitialLineItems(),
  selectedPileSize: '',
  deliverySite: { option: '', arrivalDate: null },
  payment: {
    status: '',
    depositPercent: '',
    promissoryDays: '',
    notCompletedReason: '',
  },
  deliveryNotes: '',
})

export const createInitialInstallation = (
  prefill?: SurveyPrefill
): WorkOpeningNoticeInstallationForm => ({
  headerDoc: buildPrefilledHeader(prefill),
  sendTo: '',
  items: createInitialInstallationLineItems(),
  selectedPileSize: '',
  pilingSite: { ...INITIAL_INSTALLATION_PILING_SITE },
  craneSchedule: {
    mainPile: { ...INITIAL_INSTALLATION_CRANE_SCHEDULE.mainPile },
    auspiciousPile: { ...INITIAL_INSTALLATION_CRANE_SCHEDULE.auspiciousPile },
    actualPile: { ...INITIAL_INSTALLATION_CRANE_SCHEDULE.actualPile },
    finishDate: null,
    totalWorkDays: '',
    penaltyPerDay: '',
  },
  customerDuty: createInitialInstallationCustomerDuty(),
  contract: { ...INITIAL_INSTALLATION_CONTRACT },
  payment: { ...INITIAL_INSTALLATION_FINANCE },
  signatures: {
    salesRep: { ...INITIAL_INSTALLATION_SIGNATURES.salesRep },
    salesDirector: { ...INITIAL_INSTALLATION_SIGNATURES.salesDirector },
    workOrderIssuer: { ...INITIAL_INSTALLATION_SIGNATURES.workOrderIssuer },
    directorAcknowledged: { ...INITIAL_INSTALLATION_SIGNATURES.directorAcknowledged },
  },
  rigFooter: { ...INITIAL_INSTALLATION_RIG_FOOTER },
})

export const apiToShippingForm = (
  data: any | null,
  prefill?: SurveyPrefill
): WorkOpeningNoticeShippingForm => {
  const base = createInitialShipping(prefill)
  if (!data) return base

  return {
    headerDoc: mergeHeaderPrefill(mapHeaderFromFlatApi(data), prefill),
    sendTo: data.sendTo ?? '',
    items: mapLineItemsFromApi(data.items),
    selectedPileSize: data.selectedPileSize ?? '',
    deliverySite: {
      option: data.deliverySiteOption ?? data.deliverySite?.option ?? '',
      arrivalDate: parseDate(data.deliverySiteArrivalDate ?? data.deliverySite?.arrivalDate),
    },
    payment: {
      status: data.paymentStatus ?? data.payment?.status ?? '',
      depositPercent:
        data.paymentDepositPercent != null
          ? String(data.paymentDepositPercent)
          : data.payment?.depositPercent != null
          ? String(data.payment.depositPercent)
          : '',
      promissoryDays:
        data.paymentPromissoryDays != null
          ? String(data.paymentPromissoryDays)
          : data.payment?.promissoryDays != null
          ? String(data.payment.promissoryDays)
          : '',
      notCompletedReason:
        data.paymentNotCompletedReason ?? data.payment?.notCompletedReason ?? '',
    },
    deliveryNotes: data.deliveryNotes ?? '',
  }
}

export const shippingFormToApi = (form: WorkOpeningNoticeShippingForm) => ({
  ...mapHeaderToFlatApi(form.headerDoc),
  sendTo: form.sendTo || null,
  items: mapLineItemsToApi(form.items),
  selectedPileSize: form.selectedPileSize || null,
  deliverySiteOption: form.deliverySite.option || null,
  deliverySiteArrivalDate: toIsoDate(form.deliverySite.arrivalDate),
  paymentStatus: form.payment.status || null,
  paymentDepositPercent: form.payment.depositPercent || null,
  paymentPromissoryDays: form.payment.promissoryDays || null,
  paymentNotCompletedReason: form.payment.notCompletedReason || null,
  deliveryNotes: form.deliveryNotes || null,
})

export const apiToInstallationForm = (
  data: any | null,
  prefill?: SurveyPrefill
): WorkOpeningNoticeInstallationForm => {
  const base = createInitialInstallation(prefill)
  if (!data) return base

  return {
    headerDoc: mergeHeaderPrefill(mapHeaderFromFlatApi(data), prefill),
    sendTo: data.sendTo ?? '',
    items: mapInstallationLineItemsFromApi(data.installationItems ?? data.items),
    selectedPileSize: data.selectedPileSize ?? '',
    pilingSite: {
      option: data.pilingSiteOption ?? data.pilingSite?.option ?? '',
      maturityDate: parseDate(data.pilingSiteMaturityDate ?? data.pilingSite?.maturityDate),
    },
    craneSchedule: {
      mainPile: {
        enabled: Boolean(data.craneMainPileEnabled ?? data.craneMainPile?.enabled),
        datetime: parseDate(data.craneMainPileDatetime ?? data.craneMainPile?.datetime),
      },
      auspiciousPile: {
        enabled: Boolean(data.craneAuspiciousPileEnabled ?? data.craneAuspiciousPile?.enabled),
        datetime: parseDate(data.craneAuspiciousPileDatetime ?? data.craneAuspiciousPile?.datetime),
      },
      actualPile: {
        enabled: Boolean(data.craneActualPileEnabled ?? data.craneActualPile?.enabled),
        datetime: parseDate(data.craneActualPileDatetime ?? data.craneActualPile?.datetime),
      },
      finishDate: parseDate(data.craneFinishDate ?? data.craneSchedule?.finishDate),
      totalWorkDays:
        data.craneTotalWorkDays != null
          ? String(data.craneTotalWorkDays)
          : data.craneSchedule?.totalWorkDays != null
          ? String(data.craneSchedule.totalWorkDays)
          : '',
      penaltyPerDay:
        data.cranePenaltyPerDay != null
          ? String(data.cranePenaltyPerDay)
          : data.craneSchedule?.penaltyPerDay != null
          ? String(data.craneSchedule.penaltyPerDay)
          : '',
    },
    customerDuty: {
      ...createInitialInstallationCustomerDuty(),
      ...(data.customerDuty ?? {}),
      otherText: data.customerDuty?.otherText ?? '',
    },
    contract: {
      status: data.contractStatus ?? data.contract?.status ?? '',
    },
    payment: {
      status: data.paymentStatus ?? data.payment?.status ?? '',
      depositPercent:
        data.paymentDepositPercent != null
          ? String(data.paymentDepositPercent)
          : data.payment?.depositPercent != null
          ? String(data.payment.depositPercent)
          : '',
      pileQueueDays:
        data.paymentPileQueueDays != null
          ? String(data.paymentPileQueueDays)
          : data.payment?.pileQueueDays != null
          ? String(data.payment.pileQueueDays)
          : '',
      drivingTicketDays:
        data.paymentDrivingTicketDays != null
          ? String(data.paymentDrivingTicketDays)
          : data.payment?.drivingTicketDays != null
          ? String(data.payment.drivingTicketDays)
          : '',
      notCompletedReason:
        data.paymentNotCompletedReason ?? data.payment?.notCompletedReason ?? '',
      chequeDetail: data.paymentChequeDetail ?? data.payment?.chequeDetail ?? '',
    },
    signatures: {
      salesRep: {
        printedName: data.signatureSalesRep ?? data.signatures?.salesRep?.printedName ?? '',
        date: parseDate(data.signatureSalesRepDate ?? data.signatures?.salesRep?.date),
      },
      salesDirector: {
        printedName: data.signatureSalesDirector ?? data.signatures?.salesDirector?.printedName ?? '',
        date: parseDate(data.signatureSalesDirectorDate ?? data.signatures?.salesDirector?.date),
      },
      workOrderIssuer: {
        printedName: data.signatureWorkOrderIssuer ?? data.signatures?.workOrderIssuer?.printedName ?? '',
        date: parseDate(data.signatureWorkOrderIssuerDate ?? data.signatures?.workOrderIssuer?.date),
      },
      directorAcknowledged: {
        printedName:
          data.signatureDirectorAcknowledged ?? data.signatures?.directorAcknowledged?.printedName ?? '',
        date: parseDate(
          data.signatureDirectorAcknowledgedDate ?? data.signatures?.directorAcknowledged?.date
        ),
      },
    },
    rigFooter: {
      rigOwner: data.rigOwner ?? data.rigFooter?.rigOwner ?? '',
      quantity:
        data.rigQuantity != null
          ? String(data.rigQuantity)
          : data.rigFooter?.quantity != null
          ? String(data.rigFooter.quantity)
          : '',
      hammerWeightTons:
        data.rigHammerWeightTons != null
          ? String(data.rigHammerWeightTons)
          : data.rigFooter?.hammerWeightTons != null
          ? String(data.rigFooter.hammerWeightTons)
          : '',
      rigOrganizer: data.rigOrganizer ?? data.rigFooter?.rigOrganizer ?? '',
      rigHeightMeters:
        data.rigHeightMeters != null
          ? String(data.rigHeightMeters)
          : data.rigFooter?.rigHeightMeters != null
          ? String(data.rigFooter.rigHeightMeters)
          : '',
      rigArrivalDate: parseDate(data.rigArrivalDate ?? data.rigFooter?.rigArrivalDate),
      date: parseDate(data.rigDate ?? data.rigFooter?.date),
    },
  }
}

export const installationFormToApi = (form: WorkOpeningNoticeInstallationForm) => ({
  ...mapHeaderToFlatApi(form.headerDoc),
  sendTo: form.sendTo || null,
  installationItems: mapInstallationLineItemsToApi(form.items),
  selectedPileSize: form.selectedPileSize || null,
  pilingSiteOption: form.pilingSite.option || null,
  pilingSiteMaturityDate: toIsoDate(form.pilingSite.maturityDate),
  craneMainPileEnabled: form.craneSchedule.mainPile.enabled,
  craneMainPileDatetime: toIsoDate(form.craneSchedule.mainPile.datetime),
  craneAuspiciousPileEnabled: form.craneSchedule.auspiciousPile.enabled,
  craneAuspiciousPileDatetime: toIsoDate(form.craneSchedule.auspiciousPile.datetime),
  craneActualPileEnabled: form.craneSchedule.actualPile.enabled,
  craneActualPileDatetime: toIsoDate(form.craneSchedule.actualPile.datetime),
  craneFinishDate: toIsoDate(form.craneSchedule.finishDate),
  craneTotalWorkDays: form.craneSchedule.totalWorkDays || null,
  cranePenaltyPerDay: form.craneSchedule.penaltyPerDay || null,
  customerDuty: form.customerDuty,
  contractStatus: form.contract.status || null,
  paymentStatus: form.payment.status || null,
  paymentDepositPercent: form.payment.depositPercent || null,
  paymentPileQueueDays: form.payment.pileQueueDays || null,
  paymentDrivingTicketDays: form.payment.drivingTicketDays || null,
  paymentNotCompletedReason: form.payment.notCompletedReason || null,
  paymentChequeDetail: form.payment.chequeDetail || null,
  signatureSalesRep: form.signatures.salesRep.printedName || null,
  signatureSalesRepDate: toIsoDate(form.signatures.salesRep.date),
  signatureSalesDirector: form.signatures.salesDirector.printedName || null,
  signatureSalesDirectorDate: toIsoDate(form.signatures.salesDirector.date),
  signatureWorkOrderIssuer: form.signatures.workOrderIssuer.printedName || null,
  signatureWorkOrderIssuerDate: toIsoDate(form.signatures.workOrderIssuer.date),
  signatureDirectorAcknowledged: form.signatures.directorAcknowledged.printedName || null,
  signatureDirectorAcknowledgedDate: toIsoDate(form.signatures.directorAcknowledged.date),
  rigOwner: form.rigFooter.rigOwner || null,
  rigQuantity: form.rigFooter.quantity || null,
  rigHammerWeightTons: form.rigFooter.hammerWeightTons || null,
  rigOrganizer: form.rigFooter.rigOrganizer || null,
  rigHeightMeters: form.rigFooter.rigHeightMeters || null,
  rigArrivalDate: toIsoDate(form.rigFooter.rigArrivalDate),
  rigDate: toIsoDate(form.rigFooter.date),
})
